import {ITableColumn} from '../../types';
import {ReactNode} from 'react';

export enum PaymentIntentStatus {
    canceled = 'canceled',
    processing = 'processing',
    requiresAction = 'requiresAction',
    requiresCapture = 'requiresCapture',
    requiresConfirmation = 'requiresConfirmation',
    requiresPaymentMethod = 'requiresPaymentMethod',
    succeeded = 'succeeded'
}

export enum SetupFutureUsage {
    offSession = 'offSession',
    onSession = 'onSession'
}

/**
 * Mirrors the `PaymentIntent` type in the stripe-api schema
 * (BILLING/APIS/flexinet-stripe-api/src/appsync-api/src/schema/features/paymentIntents.graphql):
 *
 *     type PaymentIntent {
 *         id: ID!
 *         amount: Int!
 *         automaticPaymentMethods: AutomaticPaymentMethods
 *         clientSecret: String
 *         created: Int
 *         currency: String!
 *         customerId: String
 *         description: String
 *         paymentMethod: String
 *         setupFutureUsage: SetupFutureUsage
 *         status: PaymentIntentStatus
 *         ... (amountCapturable, canceledAt, lastPaymentError etc. are declared
 *             on the API but NOT selected by this module's list query)
 *     }
 *
 * ⚠️ Only `id`, `amount` and `currency` are non-null. Everything else below is
 * marked optional to match — the previous shape claimed `status`, `created`,
 * `customerId` and `description` were always present, which is why the table
 * rendered blanks instead of placeholders for the rows where they are not (§13).
 */
export type PaymentIntent = {
    id: string,
    /**
     * ⚠️ MINOR UNITS (cents). 100 means $1.00.
     *
     * Kept as a NUMBER, and sorted as one. The Amount column renders
     * `amount / 100` through StripeAmount but its accessor stays on this raw
     * value: the rendered form is a string and would sort lexically, putting
     * "100.00" before "9.00". Multiply back by 100 on write.
     */
    amount: number
    automaticPaymentMethods?: {
        enabled: boolean
    }
    /** Stripe epoch in SECONDS — NOT milliseconds, and NOT an ISO string. */
    created?: number
    currency: string
    customerId?: string
    description?: string
    clientSecret?: string
    paymentMethod?: string
    status?: PaymentIntentStatus
    setupFutureUsage?: SetupFutureUsage
    /**
     * Derived client-side from `created` (seconds -> millis) so the Created
     * column DISPLAYS and SORTS the same value. Never sent to the API.
     *
     * Rendering `created` directly gives "Jan 1970" because moment reads a bare
     * number as millis — that is live bug FNP-1106 in two other modules.
     */
    createdAtMillis?: number
}


export interface IPaymentIntentTableColumn extends ITableColumn {
    render?: (item: PaymentIntent) => ReactNode;
}


export type ListPaymentIntents = {
    listPaymentIntents: { items: PaymentIntent[], nextToken?: string }
}

export type GetPaymentIntent = {
    getPaymentIntent: PaymentIntent
}

export type CreatePaymentIntent = {
    createPaymentIntent: PaymentIntent
}

export type UpdatePaymentIntent = {
    updatePaymentIntent: PaymentIntent
}

export type DeletePaymentIntent = {
    deletePaymentIntent: string
}
